import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Plane, MapPin, Users, Calendar, Building, Bus, Briefcase } from 'lucide-react';

const BookTrip = () => {
  const [tripType, setTripType] = useState('corporate');

  const tripTypes = [
    { id: 'corporate', name: 'Corporate Retreat', icon: '🏢' },
    { id: 'team', name: 'Team Building', icon: '🤝' },
    { id: 'conference', name: 'Conference', icon: '📊' },
    { id: 'incentive', name: 'Incentive Trip', icon: '🏆' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-cyan-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-2xl mb-6">
              <Plane className="h-12 w-12 text-blue-500" />
              <Briefcase className="h-10 w-10 text-cyan-500 -ml-4" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
              Plan Your Corporate Trip
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Organize memorable corporate retreats and team-building adventures
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">
              Corporate Trip Planning
            </h2>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 mb-2 flex items-center">
                    <Building className="h-4 w-4 mr-2 text-blue-500" />
                    Company Name
                  </label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                    placeholder="Enter company name" 
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-500" />
                    Contact Person
                  </label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                    placeholder="Enter contact person" 
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-500" />
                    Number of Employees
                  </label>
                  <input 
                    type="number" 
                    className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                    placeholder="Approximate count" 
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-blue-500" />
                    Preferred Dates
                  </label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                    placeholder="Date range or month" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 mb-4 text-lg font-semibold">Trip Type</label>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {tripTypes.map(type => (
                    <div
                      key={type.id}
                      className={`p-4 rounded-2xl text-center cursor-pointer transition-all duration-300 transform hover:-translate-y-1 ${
                        tripType === type.id 
                          ? 'bg-gradient-to-b from-blue-100 to-cyan-100 border-2 border-blue-400 shadow-md' 
                          : 'bg-gray-50 border border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => setTripType(type.id)}
                    >
                      <div className="text-3xl mb-2">{type.icon}</div>
                      <div className="font-medium">{type.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                  Destination Preferences
                </label>
                <input 
                  type="text" 
                  className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                  placeholder="e.g., Goa, Shimla, Dubai, etc." 
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Trip Objectives</label>
                <textarea 
                  rows="3" 
                  className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                  placeholder="What are the main objectives of this trip? (team building, training, relaxation, networking, etc.)"
                ></textarea>
              </div>

              <div>
                <label className="block text-gray-700 mb-2">Special Requirements</label>
                <textarea 
                  rows="3" 
                  className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all" 
                  placeholder="Any special requirements (meeting rooms, activities, dietary restrictions, transportation, accommodation, etc.)"
                ></textarea>
              </div>

              <div>
                <label className="block text-gray-700 mb-4 text-lg font-semibold">Services Required</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 p-4 border border-blue-200 rounded-xl cursor-pointer hover:bg-blue-50">
                    <input type="checkbox" className="h-5 w-5 text-blue-500" />
                    <div className="flex items-center">
                      <Plane className="h-5 w-5 mr-2 text-blue-500" />
                      <span>Flight Bookings</span>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-blue-200 rounded-xl cursor-pointer hover:bg-blue-50">
                    <input type="checkbox" className="h-5 w-5 text-blue-500" />
                    <div className="flex items-center">
                      <Building className="h-5 w-5 mr-2 text-blue-500" />
                      <span>Hotel Accommodation</span>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-blue-200 rounded-xl cursor-pointer hover:bg-blue-50">
                    <input type="checkbox" className="h-5 w-5 text-blue-500" />
                    <div className="flex items-center">
                      <Bus className="h-5 w-5 mr-2 text-blue-500" />
                      <span>Local Transportation</span>
                    </div>
                  </label>
                  <label className="flex items-center space-x-3 p-4 border border-blue-200 rounded-xl cursor-pointer hover:bg-blue-50">
                    <input type="checkbox" className="h-5 w-5 text-blue-500" />
                    <div className="flex items-center">
                      <Briefcase className="h-5 w-5 mr-2 text-blue-500" />
                      <span>Meeting Facilities</span>
                    </div>
                  </label>
                </div>
              </div>

              <button 
                type="submit" 
                className="w-full py-4 bg-gradient-to-r from-blue-400 to-cyan-400 text-white font-bold text-xl rounded-xl hover:shadow-xl hover:shadow-blue-200 transform hover:-translate-y-1 transition-all duration-300"
              >
                Request Trip Proposal
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Corporate Specialists Available</h3>
            <p className="mb-6 text-blue-100">Get a customized proposal within 24 hours</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-50 transition-all">
                Call Corporate Desk: +91 98765 43211
              </button>
              <button className="px-6 py-3 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-all">
                Email: corporate@jashn.com
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookTrip;